import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarPlan, CalendarDaySchedule, CalendarScheduleItem } from '../types';
import { fetchCalendarDetail } from '../services/calendarService';
import './HealthCalendarDetailPage.css';

const formatDateDisplay = (isoDate: string) => {
  const parsed = parseISODate(isoDate);
  if (!parsed) return isoDate;
  return `${parsed.getFullYear()}年${`${parsed.getMonth() + 1}`.padStart(2, '0')}月${`${parsed.getDate()}`.padStart(2, '0')}日`;
};

const parseISODate = (value?: string | null): Date | null => {
  if (!value) return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, y, m, d] = match;
    const candidate = new Date(Number(y), Number(m) - 1, Number(d));
    if (!Number.isNaN(candidate.getTime())) {
      return candidate;
    }
  }
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const formatWeekday = (date: Date | null) => {
  if (!date) return '';
  const labels = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
  return labels[date.getDay()];
};

const HealthCalendarDetailPage: React.FC = () => {
  const { calendarId } = useParams<{ calendarId: string }>();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<CalendarPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (!calendarId) {
      setError('缺少日程标识');
      setLoading(false);
      return () => {};
    }

    setLoading(true);
    fetchCalendarDetail(calendarId)
      .then((data) => {
        if (!mounted) return;
        setPlan(data);
        setError(null);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : '加载日程详情失败');
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [calendarId]);

  const planData = plan?.plan_data;
  const dailySchedule: CalendarDaySchedule[] = useMemo(() => {
    if (!planData?.daily_schedule) return [];
    return [...planData.daily_schedule].sort((a, b) => (a.date > b.date ? 1 : -1));
  }, [planData?.daily_schedule]);

  const totalEvents = useMemo(() => {
    return dailySchedule.reduce((sum, day) => sum + (day.items?.length ?? 0), 0);
  }, [dailySchedule]);

  const parsedPlanText = useMemo(() => {
    if (!plan?.plan_text) return null;
    try {
      const parsed = JSON.parse(plan.plan_text);
      return parsed;
    } catch (err) {
      return plan.plan_text;
    }
  }, [plan?.plan_text]);

  const handleBack = () => {
  navigate('/app/health-calendar');
  };

  return (
    <div className="calendar-detail-page">
      <motion.div
        className="detail-shell"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
      >
        <header className="detail-header">
          <button type="button" className="back-button" onClick={handleBack}>
            ← 返回养生日程
          </button>
          <div className="header-meta">
            <span className="badge">调养纲要</span>
            <h1>{plan ? '每日调护总览' : '加载中...'}</h1>
            <p>
              根据 AI 与中医节律生成的完整日程纲要，可作为逐日执行与自我调护的参考基础。
            </p>
          </div>
        </header>

        {loading && <div className="detail-loading">正在加载日程详情...</div>}
        {error && !loading && <div className="detail-error">{error}</div>}

        {plan && !loading && !error && (
          <>
            <section className="summary-panel">
              <div className="summary-card">
                <span>起止日期</span>
                <strong>
                  {planData?.start_date ? formatDateDisplay(planData.start_date) : '—'}
                  {' ~ '}
                  {planData?.end_date ? formatDateDisplay(planData.end_date) : '—'}
                </strong>
              </div>
              <div className="summary-card">
                <span>涵盖天数</span>
                <strong>{planData?.day_count ?? dailySchedule.length} 天</strong>
              </div>
              <div className="summary-card">
                <span>体质参考</span>
                <strong>{plan?.constitution || planData?.constitution || '自动匹配'}</strong>
              </div>
              <div className="summary-card">
                <span>季节主题</span>
                <strong>{plan?.season || planData?.season || '未指定'}</strong>
              </div>
              <div className="summary-card">
                <span>总计安排</span>
                <strong>{totalEvents} 条日程</strong>
              </div>
              <div className="summary-card">
                <span>创建时间</span>
                <strong>{plan.created_at ? new Date(plan.created_at).toLocaleString() : '—'}</strong>
              </div>
            </section>

            <section className="daily-section">
              <h2>逐日调护安排</h2>
              {dailySchedule.length === 0 ? (
                <div className="detail-empty">暂无逐日安排信息，可返回上一页重新生成。</div>
              ) : (
                <ul className="daily-list">
                  {dailySchedule.map((day) => {
                    const dayDate = parseISODate(day.date);
                    return (
                      <li key={day.date}>
                        <div className="daily-head">
                          <div>
                            <strong>{dayDate ? formatDateDisplay(day.date) : day.date}</strong>
                            <span>{formatWeekday(dayDate)}</span>
                          </div>
                          <div className="daily-focus">
                            <span>关注重点</span>
                            <strong>{day.focus || '无明确重点'}</strong>
                          </div>
                        </div>
                        {day.summary && <p className="daily-summary">{day.summary}</p>}
                        {day.tags && day.tags.length > 0 && (
                          <div className="daily-tags">
                            {day.tags.map((tag) => (
                              <span key={tag}>{tag}</span>
                            ))}
                          </div>
                        )}
                        <div className="daily-items">
                          {(day.items ?? []).length === 0 ? (
                            <div className="detail-empty">该日程暂无具体安排，可结合关注重点自行制定。</div>
                          ) : (
                            (day.items ?? []).map((item: CalendarScheduleItem, index: number) => (
                              <article key={item.event_id || `${day.date}-${index}`} className="daily-item">
                                <div className="item-time">{item.time_of_day || '整日'}</div>
                                <div className="item-body">
                                  <h3>{item.title}</h3>
                                  {item.description && <p>{item.description}</p>}
                                  <div className="item-meta">
                                    {item.category && <span className="pill category">{item.category}</span>}
                                    <span className={`pill source ${item.source === 'manual' ? 'manual' : 'ai'}`}>
                                      {item.source === 'manual' ? '手动添加' : 'AI 自动生成'}
                                    </span>
                                  </div>
                                </div>
                              </article>
                            ))
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="raw-section">
              <div className="raw-header">
                <h2>原始纲要数据</h2>
                <button
                  type="button"
                  className="toggle-raw"
                  onClick={() => setShowRaw((prev) => !prev)}
                >
                  {showRaw ? '收起原始数据' : '展开查看 JSON'}
                </button>
              </div>
              {showRaw && (
                <pre className="raw-block">
                  {typeof parsedPlanText === 'string'
                    ? parsedPlanText
                    : JSON.stringify(parsedPlanText, null, 2)}
                </pre>
              )}
            </section>

            <footer className="detail-footer">
              <Link to="/app/health-calendar" className="footer-link">
                返回日程总览
              </Link>
            </footer>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default HealthCalendarDetailPage;
